import type { JSX } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { AsyncSection } from '../components/AsyncSection.js';
import { DataTable } from '../components/DataTable.js';

interface CircleRow {
  id: string;
  name: string;
  status: string;
  recipientName: string;
  timezone: string;
  consentVersion: number;
  activeConsent: { version: number; scopes: string[] } | null;
  counts: { members: number; tasks: number; notes: number; attachments: number };
  plan: { code: string; status: string; trialEndsAt: string | null; currentPeriodEnd: string | null } | null;
}

export function CirclesPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'circles'],
    queryFn: () => api.get<{ items: CircleRow[] }>('/v1/admin/care-circles?limit=50'),
  });

  return (
    <>
      <Typography variant="h1" gutterBottom>
        Círculos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Estado operacional e contagens. O conteúdo das notas não é exposto aqui: leitura de
        conteúdo de família exige caso de suporte justificado, com motivo e prazo.
      </Typography>
      <AsyncSection query={query} emptyMessage="Nenhum círculo criado ainda.">
        {(data) => (
          <DataTable
            rows={data.items}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum círculo criado ainda."
            columns={[
              { key: 'name', label: 'Círculo', render: (row) => row.name },
              { key: 'recipient', label: 'Pessoa acompanhada', render: (row) => row.recipientName },
              {
                key: 'status',
                label: 'Estado',
                render: (row) => (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={row.status} />
                    {row.activeConsent ? (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`consentimento v${row.activeConsent.version}`}
                      />
                    ) : (
                      <Chip size="small" color="warning" variant="outlined" label="sem consentimento ativo" />
                    )}
                  </Stack>
                ),
              },
              {
                key: 'counts',
                label: 'Conteúdo',
                render: (row) =>
                  `${row.counts.members} membros · ${row.counts.tasks} tarefas · ${row.counts.notes} notas · ${row.counts.attachments} anexos`,
              },
              {
                key: 'plan',
                label: 'Plano',
                render: (row) =>
                  row.plan ? `${row.plan.code} (${row.plan.status})` : '—',
              },
              { key: 'tz', label: 'Fuso', render: (row) => row.timezone },
            ]}
          />
        )}
      </AsyncSection>
    </>
  );
}